import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building, Plus, LogIn, Edit3, Loader2, Trash2, Users, ChevronDown, ChevronUp,
  Mail, Phone, MapPin, Calendar, Shield, Eye, EyeOff, Key, UserPlus,
  GraduationCap, BookOpen, FileText, Wallet, IndianRupee, Copy, Check,
  AlertTriangle, X, Settings, Database, Globe, Server, Lock, Code,
  ExternalLink, RefreshCw, Activity, BarChart3, PieChart
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { formatCurrency, formatDate } from '../../lib/utils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';

// ── Types ──
interface Institute {
  id: number;
  name: string;
  slug: string;
  email: string;
  phone: string;
  address: string | null;
  plan: string;
  status: string;
  created_at: string;
  updated_at: string;
  student_count: number;
  user_count: number;
  users?: User[];
  stats?: InstituteStats;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  phone: string | null;
  created_at: string;
  updated_at: string;
}

interface InstituteStats {
  students: number;
  classes: number;
  batches: number;
  invoices: number;
  expenses: number;
  total_fees: number;
  total_paid: number;
  total_due: number;
  total_expenses: number;
}

interface Secrets {
  turso_url: string | null;
  turso_auth_token: string | null;
  jwt_secret: string | null;
  app_env: string;
  ocr_provider: string;
  hf_token: string | null;
  d1_database: string;
  storage_bucket: string;
  worker_name: string;
  frontend_url: string;
  api_url: string;
}

// ── Utility Components ──
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(label ? `${label} copied` : 'Copied');
  };
  return (
    <button onClick={handleCopy} className="p-1.5 hover:bg-purple-100 dark:hover:bg-purple-500/20 rounded-lg transition-all active:scale-90" title="Copy">
      {copied ? <Check className="w-3.5 h-3.5 text-lime-500" /> : <Copy className="w-3.5 h-3.5 text-light-text-muted" />}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { bg: string; text: string; dot: string }> = {
    active: { bg: 'bg-lime-50 dark:bg-lime-500/15', text: 'text-lime-700 dark:text-lime-300', dot: 'bg-lime-500' },
    paused: { bg: 'bg-amber-50 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
    deleted: { bg: 'bg-rose-50 dark:bg-rose-500/15', text: 'text-rose-700 dark:text-rose-300', dot: 'bg-rose-500' },
    admin: { bg: 'bg-purple-50 dark:bg-purple-500/15', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
    teacher: { bg: 'bg-electric-50 dark:bg-electric-500/15', text: 'text-electric-700 dark:text-electric-300', dot: 'bg-electric-500' },
  };
  const c = cfg[status] || { bg: 'bg-light-elevated dark:bg-dark-elevated', text: 'text-light-text-muted', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status}
    </span>
  );
}

function SectionTitle({ icon: Icon, title, subtitle, color }: { icon: any; title: string; subtitle?: string; color: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <div className={`w-8 h-8 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-light-text dark:text-dark-text">{title}</h3>
        {subtitle && <p className="text-[10px] text-light-text-muted">{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Create Institute Form ──
function CreateInstituteForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', email: '', phone: '', address: '', plan: 'free', adminPassword: '' });
  const [showPw, setShowPw] = useState(false);

  const createMutation = useMutation({
    mutationFn: async () => api.post('/api/institutes', form),
    onSuccess: () => {
      toast.success('Institute created successfully');
      setForm({ name: '', slug: '', email: '', phone: '', address: '', plan: 'free', adminPassword: '' });
      setOpen(false);
      onCreated();
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create institute'),
  });

  const handleSlug = (v: string) => {
    setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') });
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="gap-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 shadow-lg shadow-purple-500/25">
        <Plus className="w-4 h-4" /> New Institute
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                <Building className="w-4 h-4 text-purple-500" />
              </div>
              Create Institute
            </DialogTitle>
            <DialogDescription>Set up a new institute with admin credentials and configuration.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Institute Name *</Label>
              <Input value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); handleSlug(e.target.value.toLowerCase().replace(/\s+/g, '-')); }} placeholder="e.g. ABC Coaching" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">URL Slug *</Label>
              <Input value={form.slug} onChange={e => handleSlug(e.target.value)} placeholder="abc-coaching" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Admin Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="admin@school.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Admin Password</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={form.adminPassword} onChange={e => setForm({ ...form, adminPassword: e.target.value })} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-light-text-muted hover:text-light-text dark:hover:text-dark-text">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Plan</Label>
              <Select value={form.plan} onValueChange={v => setForm({ ...form, plan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs font-medium">Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !form.name || !form.slug} className="gap-1.5 bg-purple-500 hover:bg-purple-600">
              {createMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create Institute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Edit Institute Dialog ──
function EditInstituteDialog({ institute, open, onClose, onSaved }: { institute: Institute; open: boolean; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', plan: 'free', status: 'active', slug: '' });

  useEffect(() => {
    if (institute) {
      setForm({
        name: institute.name || '', email: institute.email || '', phone: institute.phone || '',
        address: institute.address || '', plan: institute.plan || 'free', status: institute.status || 'active', slug: institute.slug || '',
      });
    }
  }, [institute]);

  const updateMutation = useMutation({
    mutationFn: async () => api.patch(`/api/institutes/${institute.id}`, form),
    onSuccess: () => { toast.success('Institute updated'); onSaved(); onClose(); },
    onError: (err: any) => toast.error(err.message || 'Failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-electric-100 dark:bg-electric-500/20 flex items-center justify-center">
              <Edit3 className="w-4 h-4 text-electric-500" />
            </div>
            Edit Institute
          </DialogTitle>
          <DialogDescription>Update institute details and configuration.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">Plan</Label>
            <Select value={form.plan} onValueChange={v => setForm({ ...form, plan: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem><SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="pro">Pro</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="deleted">Deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} className="gap-1.5 bg-electric-500 hover:bg-electric-600">
            {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── User Form Dialog ──
function UserFormDialog({ instituteId, user, open, onClose, onSaved }: { instituteId: number; user?: User | null; open: boolean; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin', phone: '' });
  const [showPw, setShowPw] = useState(false);
  const isEdit = !!user;

  useEffect(() => {
    if (user) setForm({ name: user.name, email: user.email, password: '', role: user.role, phone: user.phone || '' });
    else setForm({ name: '', email: '', password: '', role: 'admin', phone: '' });
  }, [user]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) { const p: any = { ...form }; if (!p.password) delete p.password; return api.patch(`/api/institutes/${instituteId}/users/${user!.id}`, p); }
      return api.post(`/api/institutes/${instituteId}/users`, form);
    },
    onSuccess: () => { toast.success(isEdit ? 'User updated' : 'User created'); onSaved(); onClose(); },
    onError: (err: any) => toast.error(err.message || 'Failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
              {isEdit ? <Edit3 className="w-4 h-4 text-purple-500" /> : <UserPlus className="w-4 h-4 text-purple-500" />}
            </div>
            {isEdit ? 'Edit User' : 'Add User'}
          </DialogTitle>
          <DialogDescription>{isEdit ? 'Update user details. Leave password blank to keep current.' : 'Create a new user for this institute.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="user@example.com" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">{isEdit ? 'New Password (blank = keep current)' : 'Password *'}</Label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-light-text-muted hover:text-light-text">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="teacher">Teacher</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.name || !form.email || (!isEdit && !form.password)} className="gap-1.5 bg-purple-500 hover:bg-purple-600">
            {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isEdit ? <Check className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}{isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Institute Detail Panel ──
function InstituteDetail({ institute, onEdit, onDelete, onLoginAs }: { institute: Institute; onEdit: () => void; onDelete: () => void; onLoginAs: () => void }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'secrets' | 'config'>('overview');
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['institute-detail', institute.id],
    queryFn: async () => (await api.get<any>(`/api/institutes/${institute.id}`)).data,
  });

  const { data: secrets } = useQuery({
    queryKey: ['institute-secrets', institute.id],
    queryFn: async () => (await api.get<any>(`/api/institutes/${institute.id}/secrets`)).data,
    enabled: activeTab === 'secrets',
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => api.delete(`/api/institutes/${institute.id}/users/${userId}`),
    onSuccess: () => { toast.success('User deleted'); setDeleteUserId(null); queryClient.invalidateQueries({ queryKey: ['institute-detail', institute.id] }); queryClient.invalidateQueries({ queryKey: ['admin-institutes'] }); },
    onError: (err: any) => toast.error(err.message || 'Failed'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: number; password: string }) => api.patch(`/api/institutes/${institute.id}/users/${userId}`, { password }),
    onSuccess: () => { toast.success('Password reset successfully'); setResetPasswordUser(null); setNewPassword(''); },
    onError: (err: any) => toast.error(err.message || 'Failed'),
  });

  const stats = detail?.stats;
  const users = detail?.users || [];

  const tabs = [
    { key: 'overview' as const, label: 'Overview', icon: BarChart3 },
    { key: 'users' as const, label: 'Users', icon: Users, count: users.length },
    { key: 'secrets' as const, label: 'Secrets', icon: Lock },
    { key: 'config' as const, label: 'Config', icon: Settings },
  ];

  return (
    <div className="space-y-4">
      {/* Quick Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" className="gap-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 shadow-md shadow-purple-500/20" onClick={onLoginAs}>
          <LogIn className="w-3.5 h-3.5" /> Login As Admin
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={onEdit}>
          <Edit3 className="w-3.5 h-3.5" /> Edit
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5 text-rose-500 hover:text-rose-600 border-rose-200 dark:border-rose-500/20" onClick={onDelete}>
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-light-elevated dark:bg-dark-elevated rounded-xl">
        {tabs.map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === key
                ? 'bg-white dark:bg-dark-surface shadow-sm text-purple-600 dark:text-purple-400'
                : 'text-light-text-muted hover:text-light-text dark:hover:text-dark-text'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {count !== undefined && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${activeTab === key ? 'bg-purple-100 dark:bg-purple-500/20' : 'bg-light-border dark:bg-dark-border'}`}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div key="overview" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <InfoCard icon={Mail} label="Email" value={detail?.email || '—'} copy={detail?.email} />
              <InfoCard icon={Phone} label="Phone" value={detail?.phone || '—'} copy={detail?.phone} />
              <InfoCard icon={MapPin} label="Address" value={detail?.address || '—'} />
              <InfoCard icon={Globe} label="Slug" value={detail?.slug || '—'} copy={detail?.slug} />
              <InfoCard icon={Shield} label="Plan" value={(detail?.plan || 'free').toUpperCase()} />
              <InfoCard icon={Calendar} label="Created" value={detail?.created_at ? formatDate(detail.created_at) : '—'} />
              <InfoCard icon={Database} label="ID" value={String(detail?.id || '—')} />
              <InfoCard icon={Activity} label="Status" value={detail?.status || '—'} />
              <InfoCard icon={Calendar} label="Updated" value={detail?.updated_at ? formatDate(detail.updated_at) : '—'} />
            </div>

            {/* Stats */}
            {stats && (
              <div className="space-y-2">
                <SectionTitle icon={PieChart} title="Statistics" subtitle="Data from Turso institute database" color="bg-purple-500" />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <StatCard icon={GraduationCap} label="Students" value={stats.students} color="purple" />
                  <StatCard icon={BookOpen} label="Classes" value={stats.classes} color="electric" />
                  <StatCard icon={Users} label="Batches" value={stats.batches} color="cyan" />
                  <StatCard icon={FileText} label="Invoices" value={stats.invoices} color="gold" />
                  <StatCard icon={Wallet} label="Expenses" value={stats.expenses} color="rose" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatCard icon={IndianRupee} label="Total Fees" value={formatCurrency(stats.total_fees)} color="amber" />
                  <StatCard icon={IndianRupee} label="Collected" value={formatCurrency(stats.total_paid)} color="lime" />
                  <StatCard icon={IndianRupee} label="Due" value={formatCurrency(stats.total_due)} color="rose" />
                  <StatCard icon={IndianRupee} label="Expenses" value={formatCurrency(stats.total_expenses)} color="purple" />
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'users' && (
          <motion.div key="users" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionTitle icon={Users} title="Users" subtitle={`${users.length} user${users.length !== 1 ? 's' : ''}`} color="bg-electric-500" />
              <Button size="sm" className="gap-1.5 h-8 text-xs bg-electric-500 hover:bg-electric-600" onClick={() => { setEditingUser(null); setUserFormOpen(true); }}>
                <UserPlus className="w-3.5 h-3.5" /> Add User
              </Button>
            </div>

            {users.length === 0 ? (
              <div className="text-center py-10 bg-light-elevated dark:bg-dark-elevated rounded-xl">
                <Users className="w-10 h-10 text-light-text-muted mx-auto mb-2" />
                <p className="text-sm text-light-text-muted">No users yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((u: User) => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-light-elevated dark:bg-dark-elevated rounded-xl hover:bg-light-border dark:hover:bg-dark-border transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-light-text dark:text-dark-text truncate">{u.name}</p>
                        <StatusBadge status={u.role} />
                      </div>
                      <p className="text-[10px] text-light-text-muted truncate">{u.email}{u.phone ? ` • ${u.phone}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-0.5 ml-2">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingUser(u); setUserFormOpen(true); }} title="Edit user"><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-500" onClick={() => { setResetPasswordUser(u); setNewPassword(''); }} title="Reset password"><Key className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => setDeleteUserId(u.id)} title="Delete user"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'secrets' && (
          <motion.div key="secrets" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-3">
            <SectionTitle icon={Lock} title="Configuration & Secrets" subtitle="Environment variables and credentials (redacted for security)" color="bg-rose-500" />
            <div className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">Secrets are heavily redacted. Full values are never exposed via API for security.</p>
            </div>

            {secrets ? (
              <div className="space-y-2">
                <SecretRow icon={Server} label="Turso URL" value={secrets.turso_url || '—'} fullValue={secrets.turso_url || ''} />
                <SecretRow icon={Key} label="Turso Auth Token" value={secrets.turso_auth_token || '—'} fullValue={secrets.turso_auth_token || ''} masked />
                <SecretRow icon={Lock} label="JWT Secret" value={secrets.jwt_secret || '—'} fullValue={secrets.jwt_secret || ''} masked />
                <SecretRow icon={Code} label="HF Token" value={secrets.hf_token || '—'} fullValue={secrets.hf_token || ''} masked />
                <SecretRow icon={Globe} label="App Environment" value={secrets.app_env || '—'} fullValue={secrets.app_env || ''} />
                <SecretRow icon={Settings} label="OCR Provider" value={secrets.ocr_provider || '—'} fullValue={secrets.ocr_provider || ''} />
                <SecretRow icon={Database} label="D1 Database" value={secrets.d1_database || '—'} fullValue={secrets.d1_database || ''} />
                <SecretRow icon={Server} label="Storage Bucket" value={secrets.storage_bucket || '—'} fullValue={secrets.storage_bucket || ''} />
                <SecretRow icon={Server} label="Worker Name" value={secrets.worker_name || '—'} fullValue={secrets.worker_name || ''} />
                <SecretRow icon={ExternalLink} label="Frontend URL" value={secrets.frontend_url || '—'} fullValue={secrets.frontend_url || ''} link />
                <SecretRow icon={ExternalLink} label="API URL" value={secrets.api_url || '—'} fullValue={secrets.api_url || ''} link />
              </div>
            ) : (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-light-text-muted mx-auto" /></div>
            )}
          </motion.div>
        )}

        {activeTab === 'config' && (
          <motion.div key="config" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-3">
            <SectionTitle icon={Settings} title="Configuration" subtitle="Institute settings and metadata" color="bg-gold-500" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ConfigRow label="Institute ID" value={String(detail?.id || '—')} />
              <ConfigRow label="Name" value={detail?.name || '—'} />
              <ConfigRow label="Slug" value={detail?.slug || '—'} />
              <ConfigRow label="Plan" value={(detail?.plan || 'free').toUpperCase()} />
              <ConfigRow label="Status" value={detail?.status || '—'} />
              <ConfigRow label="Email" value={detail?.email || '—'} />
              <ConfigRow label="Phone" value={detail?.phone || '—'} />
              <ConfigRow label="Address" value={detail?.address || '—'} />
              <ConfigRow label="Created" value={detail?.created_at ? formatDate(detail.created_at) : '—'} />
              <ConfigRow label="Updated" value={detail?.updated_at ? formatDate(detail.updated_at) : '—'} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dialogs */}
      <UserFormDialog instituteId={institute.id} user={editingUser} open={userFormOpen} onClose={() => { setUserFormOpen(false); setEditingUser(null); }} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['institute-detail', institute.id] }); queryClient.invalidateQueries({ queryKey: ['admin-institutes'] }); }} />

      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-500" /> Delete User</AlertDialogTitle><AlertDialogDescription>This will permanently remove this user and all their access.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => deleteUserId && deleteUserMutation.mutate(deleteUserId)}>Delete User</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setNewPassword(''); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-amber-500" /> Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {resetPasswordUser?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-2.5 bg-light-elevated dark:bg-dark-elevated rounded-lg">
              <p className="text-[10px] text-light-text-muted">User</p>
              <p className="text-sm font-semibold text-light-text dark:text-dark-text">{resetPasswordUser?.email}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">New Password</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-light-text-muted">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}>Cancel</Button>
            <Button onClick={() => resetPasswordUser && resetPasswordMutation.mutate({ userId: resetPasswordUser.id, password: newPassword })} disabled={!newPassword || newPassword.length < 4 || resetPasswordMutation.isPending} className="gap-1.5 bg-amber-500 hover:bg-amber-600">
              {resetPasswordMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Helper Components ──
function InfoCard({ icon: Icon, label, value, copy }: { icon: any; label: string; value: string; copy?: string }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 bg-light-elevated dark:bg-dark-elevated rounded-xl">
      <Icon className="w-4 h-4 text-light-text-muted flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[9px] text-light-text-muted">{label}</p>
        <p className="text-xs font-medium text-light-text dark:text-dark-text truncate">{value}</p>
      </div>
      {copy && <CopyButton text={copy} label={label} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    purple: 'bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20',
    electric: 'bg-electric-50 dark:bg-electric-500/10 border-electric-200 dark:border-electric-500/20',
    cyan: 'bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20',
    gold: 'bg-gold-50 dark:bg-gold-500/10 border-gold-200 dark:border-gold-500/20',
    rose: 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20',
    amber: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20',
    lime: 'bg-lime-50 dark:bg-lime-500/10 border-lime-200 dark:border-lime-500/20',
  };
  const iconColor: Record<string, string> = {
    purple: 'text-purple-500', electric: 'text-electric-500', cyan: 'text-cyan-500',
    gold: 'text-gold-500', rose: 'text-rose-500', amber: 'text-amber-500', lime: 'text-lime-500',
  };
  return (
    <div className={`p-3 rounded-xl border ${colorMap[color] || colorMap.purple}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconColor[color] || iconColor.purple}`} />
        <p className="text-[10px] text-light-text-muted">{label}</p>
      </div>
      <p className="text-sm font-bold text-light-text dark:text-dark-text">{value}</p>
    </div>
  );
}

function SecretRow({ icon: Icon, label, value, fullValue, masked, link }: { icon: any; label: string; value: string; fullValue: string; masked?: boolean; link?: boolean }) {
  const [show, setShow] = useState(false);
  const displayValue = masked && !show ? value : fullValue;
  return (
    <div className="flex items-center gap-2.5 p-3 bg-light-elevated dark:bg-dark-elevated rounded-xl">
      <Icon className="w-4 h-4 text-light-text-muted flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[9px] text-light-text-muted">{label}</p>
        <p className="text-xs font-mono text-light-text dark:text-dark-text truncate">{displayValue}</p>
      </div>
      <div className="flex items-center gap-1">
        {masked && (
          <button onClick={() => setShow(!show)} className="p-1.5 hover:bg-purple-100 dark:hover:bg-purple-500/20 rounded-lg transition-colors">
            {show ? <EyeOff className="w-3.5 h-3.5 text-light-text-muted" /> : <Eye className="w-3.5 h-3.5 text-light-text-muted" />}
          </button>
        )}
        {link && fullValue && (
          <a href={fullValue} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-purple-100 dark:hover:bg-purple-500/20 rounded-lg transition-colors">
            <ExternalLink className="w-3.5 h-3.5 text-light-text-muted" />
          </a>
        )}
        <CopyButton text={fullValue} label={label} />
      </div>
    </div>
  );
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between p-2.5 bg-light-elevated dark:bg-dark-elevated rounded-xl">
      <p className="text-xs text-light-text-muted">{label}</p>
      <p className="text-xs font-medium text-light-text dark:text-dark-text">{value}</p>
    </div>
  );
}

// ── Main Page ──
export default function AdminInstitutesPage() {
  const queryClient = useQueryClient();
  const { assumeInstitute, user } = useAuthStore();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editInstitute, setEditInstitute] = useState<Institute | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data: institutes, isLoading } = useQuery({
    queryKey: ['admin-institutes'],
    queryFn: async () => (await api.get<any>('/api/institutes')).data || [],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/institutes/${id}`),
    onSuccess: () => { toast.success('Institute deleted'); setDeleteConfirm(null); setExpandedId(null); queryClient.invalidateQueries({ queryKey: ['admin-institutes'] }); },
    onError: (err: any) => toast.error(err.message || 'Failed'),
  });

  if (user?.role !== 'super_admin') {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-rose-50 dark:bg-rose-500/10 mx-auto mb-4 flex items-center justify-center"><Shield className="w-8 h-8 text-rose-400" /></div>
        <p className="text-lg font-semibold text-light-text dark:text-dark-text">Access Denied</p>
        <p className="text-sm text-light-text-muted mt-1">Super admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-6">
      {/* Hero Header */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 p-5 sm:p-7 text-white shadow-xl overflow-hidden relative">
        <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10 blur-xl" />
        <div className="absolute -left-8 bottom-0 h-24 w-24 rounded-full bg-indigo-400/15 blur-xl" />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-purple-200/80">Super Admin Panel</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-black leading-tight">Institutes</h1>
            <p className="text-sm text-white/70 mt-1">Manage all institutes, users, secrets, and configuration.</p>
          </div>
          <CreateInstituteForm onCreated={() => queryClient.invalidateQueries({ queryKey: ['admin-institutes'] })} />
        </div>
      </div>

      {/* Institute List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-28 rounded-2xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />)}
        </div>
      ) : (institutes || []).length === 0 ? (
        <div className="text-center py-20 bg-light-surface dark:bg-dark-surface border border-dashed border-light-border dark:border-dark-border rounded-2xl">
          <div className="w-16 h-16 rounded-2xl bg-purple-50 dark:bg-purple-500/10 mx-auto mb-4 flex items-center justify-center"><Building className="w-8 h-8 text-purple-400" /></div>
          <p className="text-sm font-semibold text-light-text dark:text-dark-text">No institutes yet</p>
          <p className="text-xs text-light-text-muted mt-1">Create your first institute to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(institutes || []).map((inst: Institute, index: number) => {
            const isExpanded = expandedId === inst.id;
            return (
              <motion.div key={inst.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <button onClick={() => setExpandedId(isExpanded ? null : inst.id)} className="w-full p-4 sm:p-5 text-left">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-md shadow-purple-500/20">
                          <Building className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-light-text dark:text-dark-text text-sm sm:text-base">{inst.name}</p>
                          <p className="text-[10px] text-light-text-muted">{inst.slug} • {inst.email}</p>
                        </div>
                        <StatusBadge status={inst.status} />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="hidden sm:flex items-center gap-4 text-[10px]">
                        <span className="text-light-text-muted"><strong className="text-light-text dark:text-dark-text">{inst.student_count || 0}</strong> students</span>
                        <span className="text-light-text-muted"><strong className="text-light-text dark:text-dark-text">{inst.user_count || 0}</strong> users</span>
                        <span className="text-light-text-muted uppercase font-medium"><strong className="text-light-text dark:text-dark-text">{inst.plan || 'free'}</strong></span>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-light-text-muted" /> : <ChevronDown className="w-5 h-5 text-light-text-muted" />}
                    </div>
                  </div>
                  <div className="flex sm:hidden items-center gap-3 mt-2 text-[10px]">
                    <span className="text-light-text-muted"><strong>{inst.student_count || 0}</strong> students</span>
                    <span className="text-light-text-muted"><strong>{inst.user_count || 0}</strong> users</span>
                    <span className="text-light-text-muted uppercase"><strong>{inst.plan || 'free'}</strong></span>
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-4 sm:px-5 pb-5 border-t border-light-border dark:border-dark-border pt-4">
                        <InstituteDetail institute={inst} onEdit={() => setEditInstitute(inst)} onDelete={() => setDeleteConfirm(inst.id)} onLoginAs={() => assumeInstitute(inst.id, inst.name)} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Edit Institute Dialog */}
      {editInstitute && (
        <EditInstituteDialog institute={editInstitute} open={!!editInstitute} onClose={() => setEditInstitute(null)} onSaved={() => { queryClient.invalidateQueries({ queryKey: ['admin-institutes'] }); queryClient.invalidateQueries({ queryKey: ['institute-detail', editInstitute.id] }); }} />
      )}

      {/* Delete Institute Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-500" /> Delete Institute</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this institute and all associated data. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)}>Delete Institute</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
